export const EXPLORER_VIEW_IDS = [
    "shipmentsAllOpen",
    "shipmentsProcuring",
    "shipmentsScheduled",
    "shipmentsInTransit",
    "shipmentsLate",
    "shipmentsDelivered",
    "shipmentsMine",
    "shipmentsMissingPod",
    "shipmentsMissingBol",
    "shipmentsNoDeliveryDate",
    "shipmentsDueThisWeek",
    "shipmentsOnHold",
    "salesOrdersOpen",
    "salesOrdersPartlyFulfilled",
    "salesOrdersDueThisWeek",
    "salesOrdersFulfilled",
    "quotesPending",
    "quotesAccepted",
    "quotesExpired",
    "productsActive",
    "productsBelowReorderPoint",
    "productsDiscontinued",
    "vendorsCarriers",
    "vendorsSuppliers",
    "vendorsWarehouses",
    "vendorsInsuranceExpiring",
    "addressesCustomers",
    "addressesWarehouses",
    "addressesAppointmentRequired",
    "contactsNamed",
    "contactsNoNameYet",
    "contactsAddedThisWeek",
] as const

export type ExplorerViewId = (typeof EXPLORER_VIEW_IDS)[number]

export const EXPLORER_TYPE_IDS = [
    "shipments",
    "salesOrders",
    "quotes",
    "products",
    "vendors",
    "addressBook",
    "contacts",
    "workspace",
] as const
export type ExplorerTypeId = (typeof EXPLORER_TYPE_IDS)[number]

export const EXPLORER_TYPE_API_TYPE = {
    shipments: "shipments",
    salesOrders: "sales-orders",
    quotes: "quotes",
    products: "products",
    vendors: "vendors",
    addressBook: "address-book",
    contacts: "contacts",
    workspace: "workspace",
} as const satisfies Record<ExplorerTypeId, string>

export const POSTGRES_EXPLORER_TYPE_IDS = [
    "quotes",
    "salesOrders",
    "products",
    "vendors",
    "addressBook",
] as const satisfies readonly ExplorerTypeId[]

export type PostgresExplorerTypeId = (typeof POSTGRES_EXPLORER_TYPE_IDS)[number]

// Contacts live only in Postgres: Explorer lists and counts them there, and
// the Typesense shadow every other type still runs skips them.
export const isIndexedInTypesense = (typeId: ExplorerTypeId): boolean =>
    typeId !== "contacts"

export const isPostgresExplorerType = (
    typeId: ExplorerTypeId
): typeId is PostgresExplorerTypeId =>
    (POSTGRES_EXPLORER_TYPE_IDS as readonly ExplorerTypeId[]).includes(typeId)

export const EXPLORER_VIEWS_BY_TYPE: Record<
    ExplorerTypeId,
    readonly ExplorerViewId[]
> = {
    shipments: [
        "shipmentsAllOpen",
        "shipmentsProcuring",
        "shipmentsScheduled",
        "shipmentsInTransit",
        "shipmentsLate",
        "shipmentsDelivered",
        "shipmentsMine",
        "shipmentsMissingPod",
        "shipmentsMissingBol",
        "shipmentsNoDeliveryDate",
        "shipmentsDueThisWeek",
        "shipmentsOnHold",
    ],
    salesOrders: [
        "salesOrdersOpen",
        "salesOrdersPartlyFulfilled",
        "salesOrdersDueThisWeek",
        "salesOrdersFulfilled",
    ],
    quotes: ["quotesPending", "quotesAccepted", "quotesExpired"],
    products: [
        "productsActive",
        "productsBelowReorderPoint",
        "productsDiscontinued",
    ],
    vendors: [
        "vendorsCarriers",
        "vendorsSuppliers",
        "vendorsWarehouses",
        "vendorsInsuranceExpiring",
    ],
    addressBook: [
        "addressesCustomers",
        "addressesWarehouses",
        "addressesAppointmentRequired",
    ],
    contacts: ["contactsNamed", "contactsNoNameYet", "contactsAddedThisWeek"],
    workspace: [],
}

export const explorerTypeForView = (viewId: ExplorerViewId): ExplorerTypeId => {
    for (const typeId of EXPLORER_TYPE_IDS) {
        if (EXPLORER_VIEWS_BY_TYPE[typeId].includes(viewId)) return typeId
    }
    return "shipments"
}

export const OPEN_SHIPMENT_STATUSES = [
    "pending",
    "procuring",
    "scheduled",
    "inTransit",
] as const

export const INSURANCE_EXPIRING_WINDOW_DAYS = 60

export const MAX_EXPLORER_SAVED_VIEWS_PER_USER = 200
